import { Text, Box } from "@chakra-ui/react"
import React, { useEffect, useRef } from "react"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import { useQueryClient } from "react-query"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import ImageUpload from "components/ReactHookForm/ImageUpload"
import { HeroImageFormValues } from "pages/EventShow/WebPage/types"

interface Props {
  eventId: string
  coverImageUrl: string
  onClose: () => void
  onHide: () => void
  onChange?: (values: HeroImageFormValues) => void
}

const HeroImage = ({
  eventId, coverImageUrl, onClose, onHide, onChange,
}: Props) => {
  const queryClient = useQueryClient()
  const previewUrlRef = useRef<string | null>(null)

  // Clean up object URL on unmount
  useEffect(() => () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
  }, [])

  const handleClose = () => {
    // Clean up preview URL when canceling
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    // Reset preview to original state
    onChange?.({ coverImageUrl: coverImageUrl || "" } as HeroImageFormValues)
    onClose()
  }

  const onSubmit = (values) => api.putWebsite(eventId, values)
    .then(({ data }) => {
      // Clean up preview URL after successful upload
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = null
      }
      updateWebsite(queryClient, eventId, data)
      onHide()
    })

  const handleFormChange = (values: { coverImage?: File | string | null }) => {
    // Create preview URL when a file is selected
    if (values.coverImage && values.coverImage instanceof File) {
      // Clean up previous preview URL
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
      }
      // Create new preview URL
      previewUrlRef.current = URL.createObjectURL(values.coverImage)
      onChange?.({ coverImageUrl: previewUrlRef.current } as HeroImageFormValues)
    } else if (values.coverImage === null) {
      // Image was removed
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = null
      }
      onChange?.({ coverImageUrl: "" } as HeroImageFormValues)
    }
  }

  return (
    <ModalForm
      title="Hero Image"
      initialValues={{}}
      onSubmit={onSubmit}
      onClose={handleClose}
      disableSubmitWhenNotDirty
      onChange={handleFormChange}
    >
      {({ control }) => (
        <>
          <Text fontSize="md" mb={8}>
            Upload an image to represent your event. The image may be cropped or resized
            {" "}
            based on the selected layout and screen size.
          </Text>
          <Box>
            <ImageUpload
              alt="hero image"
              name="coverImage"
              control={control}
              imageUrl={coverImageUrl ? `${coverImageUrl}?w=320` : ""}
              imageWidth={320}
            />
            <Text fontSize="sm" mt={3}>
              For best results, upload an image at least 1920x1080. PNG or JPG, up to 5 MB.
            </Text>
          </Box>
        </>
      )}
    </ModalForm>
  )
}

export default HeroImage
